import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { RentalObjectCategory } from '../../model/const/enums';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LocalDate } from '../../../core/model/local-date';
import { TransferType, transferTypeToString } from '../../model/const/transfer-type';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethod, InvoiceDeliveryMethodToString } from '../../../invoice/model/const/enum';
import { Money } from '../../../core/model/money';
import { PostalAddress } from '../../../common/model/postal-address';
import { TenantService } from '../../service/data/tenant.service';
import { PreTransferActionModel } from '../../model/Transfer/pre-transfer-action-model';
import { CooperativeHousingTransferService } from '../../service/data/cooperative-housing-transfer.service';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-selection-item';
import { MoneyValidator } from '../../../core/service/money-validator';
import { numberValue } from '../../../core/helper/number-value';

@Component({
    selector: 'app-brf-pre-transfer-contract',
    templateUrl: './brf-pre-transfer.component.html',
    styleUrls: ['./brf-pre-transfer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BrfPreTransferComponent implements OnDestroy, OnInit {
  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _fcf: FormControlExFactory,
    private readonly _service: CooperativeHousingTransferService,
    private readonly _navService: NavigationService,
    private readonly _fb: FormBuilder,
    public readonly tenantService: TenantService
  ) {}

  private _subscription: Subscription = new Subscription();
  dataContext: PreTransferActionModel;
  form: FormGroup;

  get showBuyAmount(): boolean {
    if (!this.showSellers) return false;
    const type = this.form.controls['transferType'].value;
    return type === TransferType.Purchase || type === TransferType.ForcedSale;
  }

  get showLaterBuyAmount(): boolean {
    const type = this.form.controls['laterTransferType'].value;
    return type === TransferType.Purchase || type === TransferType.ForcedSale;
  }

  transferTypes: SelectionItem[] = [
    new SelectionItem(transferTypeToString(TransferType.Purchase), TransferType.Purchase),
    new SelectionItem(transferTypeToString(TransferType.PartitionOfProperty), TransferType.PartitionOfProperty),
    new SelectionItem(transferTypeToString(TransferType.Gift), TransferType.Gift),
    new SelectionItem(transferTypeToString(TransferType.Inheritance), TransferType.Inheritance),
    new SelectionItem(transferTypeToString(TransferType.ForcedSale), TransferType.ForcedSale),
  ];

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
  }

  ngOnInit() {
    this._subscription.add(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('rentalObjectId');
            return this._service.getPreTransferDefaults(id);
          })
        )
        .subscribe(res => {
          this.dataContext = res;
          this.form = this.createForm();
          this.updateValidators();
        })
    );
  }

  addSeller() {
    this.sellers.push(this.createSellerForm());
  }

  removeSeller(index: number) {
    this.sellers.removeAt(index);
  }

  invoiceDeliveryMethods = [
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual), value: InvoiceDeliveryMethod.Manual, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B), value: InvoiceDeliveryMethod.EInvoiceB2B, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B) },
  ];

  get showSellers(): boolean {
    return this.form && !this.form.controls['isFirstTransfer'].value;
  }

  createForm(): FormGroup {
    /*
      isFirstTransfer: boolean;
      rentalObjectId: string;
      leaseAgreementNumber: string;
      currencyCode: string;
      sellers: TransferSeller[] = [];
      buyers: TransferBuyer[] = [];
      agreementDate: LocalDate = null;
      fromDate: LocalDate = null;
      purchaseAmount: Money = null;
      laterPurchaseAmount: Money;
      transferType: TransferType;
      laterTransferType: TransferType;
  */
    this.sellers = this._fb.array([this.createSellerForm()]);
    let form = this._fb.group({
      isFirstTransfer: this._fcf.create('COOPERATIVE_HOUSING.PRE_TRANSFER_FIRST_CONTRACT', false),
      rentalObjectId: this._fcf.createHidden(this.dataContext.rentalObjectId),
      buyers: this.createBuyerForms(),
      leaseAgreementNumber: this._fcf.createMandatory('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', this.dataContext.leaseAgreementNumber),
      agreementDate: this._fcf.createMandatory('LEASE_AGREEMENT.AGREEMENT_DATE', this.dataContext.agreementDate),
      fromDate: this._fcf.createMandatory('COOPERATIVE_HOUSING.ACCESS_DATE', this.dataContext.fromDate),
      purchaseAmount: this._fcf.createMandatory('COOPERATIVE_HOUSING.TRANSFER_AMOUNT', new Money(this.dataContext.currencyCode, null)),
      transferType: this._fcf.createMandatory('COOPERATIVE_HOUSING.TRANSFER_TYPE', this.dataContext.transferType),
      sellers: this.sellers,
      laterLeaseAgreementNumber: this._fcf.createReadonly(
        'LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER',
        this.dataContext.laterLeaseAgreementNumber
      ),
      laterPurchaseAmount: this._fcf.createMandatory(
        'COOPERATIVE_HOUSING.TRANSFER_AMOUNT',
        new Money(this.dataContext.currencyCode, this.dataContext.laterPurchaseAmount?.amount)
      ),
      laterTransferType: this._fcf.createMandatory('COOPERATIVE_HOUSING.TRANSFER_TYPE', this.dataContext.laterTransferType),
    });
    this._subscription.add(
      form.controls['isFirstTransfer'].valueChanges.subscribe(val => {
        if (val) {
          this.sellers.clear();
        } else {
          this.sellers.push(this.createSellerForm());
        }
        this.updateValidators();
      })
    );
    this._subscription.add(form.controls['transferType'].valueChanges.subscribe(() => this.updateValidators()));
    this._subscription.add(form.controls['laterTransferType'].valueChanges.subscribe(() => this.updateValidators()));
    return form;
  }

  private updateValidators() {
    let registerSeller = !this.form.controls['isFirstTransfer'].value;
    for (let field of ['leaseAgreementNumber', 'agreementDate', 'fromDate', 'transferType']) {
      this.form.controls[field].setValidators(registerSeller ? Validators.required : null);
      this.form.controls[field].updateValueAndValidity();
    }

    const required = [MoneyValidator.required, MoneyValidator.moneyMin(1)];
    this.form.controls['purchaseAmount'].setValidators(this.showBuyAmount ? required : null);
    this.form.controls['purchaseAmount'].updateValueAndValidity();
    this.form.controls['laterPurchaseAmount'].setValidators(this.showLaterBuyAmount ? required : null);
    this.form.controls['laterPurchaseAmount'].updateValueAndValidity();
  }

  emptyAddressModel: PostalAddress;
  buyers: FormArray;
  sellers: FormArray;

  createBuyerForms(): FormArray {
    this.buyers = this._fb.array([]);
    for (let buyer of this.dataContext.buyers) {
      let form = this._fb.group({
        share: this._fcf.createReadonly('COOPERATIVE_HOUSING.OWNED_SHARE', buyer.share),
        name: this._fcf.createReadonly('COOPERATIVE_HOUSING.OWNER', buyer.name),
      });
      this.buyers.push(form);
    }
    return this.buyers;
  }

  createSellerForm(): FormGroup {
    let form = this._fb.group({
      contactId: this._fcf.createMandatory('COOPERATIVE_HOUSING.OWNER', null),
      share: this._fcf.createWithOptions('COOPERATIVE_HOUSING.OWNED_SHARE', 0, {
        required: true,
        validator: Validators.compose([Validators.min(0.01), c => this._validateShareSum(c)]),
      }),
    });
    this._subscription.add(
      form.controls['share'].valueChanges.subscribe(val => {
        for (let f of this.sellers.controls.map(c => c as FormGroup)) {
          this.updateValidity(f);
        }
      })
    );
    return form;
  }

  sellerSelected(item: AutoCompleteSelectionItem, formIndex: number) {}

  updateValidity(group: FormGroup): void {
    for (const field in group.controls) {
      const abstractControl = group.controls[field];
      if (abstractControl instanceof AbstractControl) {
        abstractControl.updateValueAndValidity({ emitEvent: false });
        abstractControl.markAllAsTouched();
      }
      if (abstractControl instanceof FormArray) {
        for (let form of abstractControl.controls.map(c => c as FormGroup)) this.updateValidity(form);
      }
    }
    this.form.markAllAsTouched();
  }

  get isDwelling(): boolean {
    return this.form && this.form.controls['objectCategory'].value === RentalObjectCategory.Dwelling;
  }

  get isDivisionOfProperty(): boolean {
    return this.form && this.form.controls['transferType'].value == TransferType.PartitionOfProperty;
  }

  private _validateShareSum(control: AbstractControl): ValidationErrors {
    if (!this.form || this.form.controls['isFirstTransfer'].value) return null;

    let share = this.sellers.controls
      .map(c => numberValue((c as FormGroup).controls['share'].value))
      .filter(share => share)
      .reduce((a, b) => a + b, 0);
    if (share != 100) {
      return { function: { message: 'COOPERATIVE_HOUSING.VALIDATION.TOTAL_SHARE_MUST_BE_100', params: {} } };
    }
    return null;
  }

  cancel() {
    this._navService.navigateBack();
  }
  private posting: boolean = false;
  private postSub: Subscription;
  onSubmit() {
    this.updateValidity(this.form);

    if (this.posting) {
      return;
    }

    if (this.form.valid) {
      this.posting = true;
      let context = this.form.value;
      if (this.postSub) this.postSub.unsubscribe();
      this.postSub = this._service
        .createPreTransfer(context)
        .pipe(
          finalize(() => {
            this.posting = false;
          })
        )
        .subscribe(res => {
          this._navService.navigateBack();
        });
    }
  }
}
